import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter } from '@tanstack/react-router';
import { AppShell } from './components/AppShell.js';
import { WorldShell } from './components/WorldShell.js';
import { AuthPage } from './pages/AuthPage.js';
import { HivePage } from './pages/HivePage.js';
import { LandingPage } from './pages/LandingPage.js';
import { MapPage } from './pages/MapPage.js';
import { MovesPage } from './pages/MovesPage.js';
import { AdminPage } from './pages/AdminPage.js';
import { AlliancePage } from './pages/AlliancePage.js';
import { ChatPage } from './pages/ChatPage.js';
import { PlaceholderPage } from './pages/PlaceholderPage.js';
import { ReportsPage } from './pages/ReportsPage.js';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: AppShell });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => <AuthPage mode="login" />,
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: () => <AuthPage mode="register" />,
});

export const worldRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w/$worldId',
  component: WorldShell,
});
// Karte ist der Startbildschirm einer Welt, der Stock liegt unter /stock.
const num = (v: unknown) =>
  v === undefined || v === '' || Number.isNaN(Number(v)) ? undefined : Number(v);
const mapRoute = createRoute({
  getParentRoute: () => worldRoute,
  path: '/',
  component: MapPage,
  validateSearch: (s: Record<string, unknown>): { q?: number; r?: number } => {
    const out: { q?: number; r?: number } = {};
    const q = num(s.q);
    const r = num(s.r);
    if (q !== undefined) out.q = q;
    if (r !== undefined) out.r = r;
    return out;
  },
});
const hiveRoute = createRoute({
  getParentRoute: () => worldRoute,
  path: '/stock',
  component: HivePage,
});
const movesRoute = createRoute({
  getParentRoute: () => worldRoute,
  path: '/zuege',
  component: MovesPage,
});
const chatRoute = createRoute({
  getParentRoute: () => worldRoute,
  path: '/chat',
  component: ChatPage,
  validateSearch: (s: Record<string, unknown>): { c?: number } => {
    const c = num(s.c);
    return c !== undefined ? { c } : {};
  },
});
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminPage,
});
const allianceRoute = createRoute({
  getParentRoute: () => worldRoute,
  path: '/stand',
  component: AlliancePage,
});
const reportsRoute = createRoute({
  getParentRoute: () => worldRoute,
  path: '/berichte',
  component: ReportsPage,
  validateSearch: (s: Record<string, unknown>): { token?: string } =>
    typeof s.token === 'string' && s.token ? { token: s.token } : {},
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  adminRoute,
  worldRoute.addChildren([mapRoute, hiveRoute, movesRoute, chatRoute, allianceRoute, reportsRoute]),
]);

export const router = createRouter({ routeTree, context: { queryClient: undefined! } });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
